/**
 * 手机号冲突校验
 */
import React, { useImperativeHandle, useRef, useState } from 'react';
import { Table, message, Modal, Button } from 'antd';
import { ColumnProps } from 'antd/es/table';

import ThrottleButton from '@/components/Reservation/ThrottleButton';
import NumberInput from '@/components/Reservation/NumberInput';
import getRandomId from '@/utils/uuid';
import DateJS from '@/utils/date';
import { renderIntlLabel } from '@/utils';
import { checkUserPhoneNo, sendSMSCheckCode, verifyUserPhoneNo } from '@/api/guest';
import { ContractType, ContractTypeMap } from '@/config';
import { formatAreaMobileStr } from '../BaseMobileInput/helper';

import SMSCodeButton from './SMSCodeButton';
import styles from './index.module.scss';

// 入参
interface InParameterItemInfo {
  wyuid?: string; // uid
  wyuId?: string; // uid
  name: string;
  mobile: string;
  email: string;
}
interface BaseItemInfo {
  contractType: ContractType;
  name: string; // 姓名
  content: string; // 手机号或邮箱
  wyuid?: string; // uid
  wyuId?: string; // uid
}

export interface PhoneCheckModalRef {
  showPhoneCheck: (userList: Partial<InParameterItemInfo>[]) => Promise<{ success: boolean }>;
}

interface IProps {
  innerRef: React.Ref<PhoneCheckModalRef>;
}

enum ActionType {
  None, //
  Update = 1, // 绑定新手机号
  Change = 2, // 换绑
}

interface TableRecord extends BaseItemInfo {
  itemId: string; // 唯一标识
  actionType: ActionType; // 类型
  smsCode: string; // 验证码
  smsSendTime: number; // 验证码发送时间
  oriContent: string; // 原手机号
  oriName: string; // 原登记用户
  oriWyuid: string; // 原uid
  errorMessage: string; // 错误文案
  groupInfo: Array<{ oriName: string; oriWyuid: string }>;
}

const formatTableData = (list: TableRecord[]) => {
  const updateDataList = []; // 绑定新手机号
  const changeDataList = []; // 换绑
  list.forEach((item) => {
    if (item.actionType === ActionType.Update) {
      updateDataList.push(item);
    }
    if (item.actionType === ActionType.Change) {
      changeDataList.push(item);
    }
  });

  return { updateDataList, changeDataList };
};

const Index: React.FC<IProps> = (props) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoadingStatus] = useState(false);
  const [dataSource, setDataSource] = useState<TableRecord[]>([]);
  const callbackRef = useRef<(value: { success: boolean }) => void>();

  // 取消
  const callBindCancel = () => {
    setVisible(false);
    setLoadingStatus(false);
    callbackRef.current({ success: false });
  };

  // 没有冲突
  const callNoChange = () => {
    setVisible(false);
    setLoadingStatus(false);
    callbackRef.current({ success: true });
  };

  // 绑定成功
  const callBindSuccess = () => {
    setVisible(false);
    setLoadingStatus(false);
    callbackRef.current({ success: true });
  };

  // 批量发送验证码
  const handleBatchSendClick = async () => {
    setLoadingStatus(true);
    try {
      const params = [];
      const updateMap: Record<string, boolean> = {};
      dataSource.forEach((record) => {
        // 手机号有冲突 且超过60秒
        if (
          record.actionType !== ActionType.None &&
          DateJS().diff(record.smsSendTime, 'second') >= 60
        ) {
          updateMap[record.itemId] = true;
          params.push({
            bizType: record.actionType,
            name: record.name,
            oriName: record.oriName,
            oriContent: record.oriContent,
            oriWyuid: record.oriWyuid,
            content: record.content,
            wyuid: record.wyuid,
            contractType: record.contractType,
          });
        }
      });
      if (params.length) {
        await sendSMSCheckCode(params);
        const smsSendTime = Date.now();
        setDataSource(
          dataSource.map((item) => {
            if (updateMap[item.itemId]) {
              return { ...item, smsSendTime };
            }
            return item;
          }),
        );
        message.success(renderIntlLabel('bill_msg_send_verification_code_successfully'));
      }
    } catch (e) {
      //
    }
    setLoadingStatus(false);
  };

  // 发送验证码
  const handleItemSendClick = async (record: TableRecord) => {
    setLoadingStatus(true);
    try {
      await sendSMSCheckCode([
        {
          bizType: record.actionType,
          name: record.name,
          oriName: record.oriName,
          oriContent: record.oriContent,
          oriWyuid: record.oriWyuid,
          content: record.content,
          wyuid: record.wyuid,
          contractType: record.contractType,
        },
      ]);
      const targetId = record.itemId;
      message.success(renderIntlLabel('bill_msg_send_verification_code_successfully'));
      setDataSource(
        dataSource.map((item) => {
          if (item.itemId === targetId) {
            return { ...item, smsSendTime: Date.now() };
          }
          return item;
        }),
      );
    } catch (e) {
      //
    }
    setLoadingStatus(false);
  };

  // 校验输入
  const formatVerifyParams = () => {
    const params = [];
    const messageMap: Record<string, string> = {};
    dataSource.forEach((item) => {
      if (item.actionType !== ActionType.None) {
        if (!item.smsCode) {
          messageMap[item.itemId] = renderIntlLabel('bill_msg_please_input_verification_code');
          return;
        }
        params.push({
          code: item.smsCode,
          content: item.content,
          wyuid: item.wyuid,
          contractType: item.contractType,
        });
      }
    });
    if (Object.keys(messageMap).length) {
      setDataSource(
        dataSource.map((item) => {
          const errorMessage = messageMap[item.itemId];
          if (errorMessage) {
            return {
              ...item,
              errorMessage,
            };
          }
          return item;
        }),
      );
      return [];
    }
    return params;
  };

  // 确定绑定
  const handleConfirmClick = async () => {
    setLoadingStatus(true);
    const params = formatVerifyParams();
    if (params.length) {
      try {
        const res = await verifyUserPhoneNo(params);
        const resultMap = {};
        let hasError = false;
        res.data.forEach((item) => {
          resultMap[item.wyuid] = item;
          if (item.result === 1) {
            hasError = true;
          }
        });
        if (hasError) {
          setDataSource(
            dataSource.map((item) => {
              const result = resultMap[item.wyuid];
              if (result && result.result === 1) {
                return {
                  ...item,
                  errorMessage: result.msg,
                };
              }
              return item;
            }),
          );
        } else {
          message.success(renderIntlLabel('guest_msg_binding_bind_successfully'));
          callBindSuccess();
        }
      } catch (e) {
        //
      }
    }
    setLoadingStatus(false);
  };

  // 校验是否需要更新
  const showPhoneCheck = async (list: InParameterItemInfo[]) => {
    const asyncResult = new Promise<{ success: boolean }>((resolve) => {
      callbackRef.current = resolve;
    });

    setLoadingStatus(true);
    // 需要绑定的列表
    let updateList = [];
    try {
      const params = [];
      list.forEach((item) => {
        const uid = item.wyuid || item.wyuId;
        if (uid && (item.mobile || item.email)) {
          params.push({ name: item.name, phone: item.mobile, email: item.email, wyuid: uid });
        }
      });
      if (params.length) {
        const res = await checkUserPhoneNo(params);
        updateList = res.data;
      }
    } catch (e) {
      callBindCancel();
      return asyncResult;
    }

    // 列表不为空
    if (updateList && updateList.length) {
      setVisible(true);
      setDataSource(
        updateList.map((item) => {
          return {
            itemId: getRandomId(),
            contractType: item.contractType,
            name: item.name,
            content: item.content,
            wyuid: item.wyuid,
            oriName: item.oriName,
            oriContent: item.oriContent,
            oriWyuid: item.oriWyuid,
            groupInfo: item.groupInfo,
            actionType: item.bizType,
            smsCode: '',
            smsSendTime: 0,
            errorMessage: '',
          };
        }),
      );
    } else {
      // 不需要校验
      callNoChange();
    }
    setLoadingStatus(false);

    return asyncResult;
  };

  useImperativeHandle(props.innerRef, () => ({ showPhoneCheck }), []);

  // 修改验证码
  const handleCodeChange = (targetId: string, val: string) => {
    // 6位数字
    if (!/^\d{0,6}$/.test(val)) return;
    setDataSource(
      dataSource.map((item) => {
        if (item.itemId === targetId) {
          return { ...item, smsCode: val, errorMessage: '' };
        }
        return item;
      }),
    );
  };

  // 验证码输入
  const renderCodeInput = (item: TableRecord) => {
    return (
      <div className={item.errorMessage ? styles.codeStatusError : styles.codeStatusNormal}>
        <NumberInput
          placeholder={renderIntlLabel('app_search_form_placeholder_prefix')}
          value={item.smsCode}
          onChange={(val) => handleCodeChange(item.itemId, val)}
        />
        <div className={styles.codeStatusMessage}>{item.errorMessage}</div>
      </div>
    );
  };

  // 验证码发送按钮
  const renderCodeButton = (item: TableRecord) => {
    return <SMSCodeButton value={item.smsSendTime} onClick={() => handleItemSendClick(item)} />;
  };

  const { updateDataList, changeDataList } = formatTableData(dataSource);

  const updateColumns: ColumnProps<TableRecord>[] = [
    { title: renderIntlLabel('order_roomorder_name'), dataIndex: 'name', width: 140 },
    {
      title: renderIntlLabel('channel_type'),
      dataIndex: 'contractType',
      render: (val) => ContractTypeMap[val],
      width: 140,
    },
    {
      title: renderIntlLabel('channel_approver_info_edit_before'),
      dataIndex: 'oriContent',
      render: (val, record) =>
        record?.contractType === ContractType.MOBILE ? formatAreaMobileStr(val) : val,
    },
    {
      title: renderIntlLabel('channel_approver_info_edit_after'),
      dataIndex: 'content',
      render: (val, record) =>
        record?.contractType === ContractType.MOBILE ? formatAreaMobileStr(val) : val,
    },
    {
      title: renderIntlLabel('order_roomorder_new_mobile_verification_code'),
      key: 'smsCode',
      width: 140,
      render: renderCodeInput,
    },
    {
      title: renderIntlLabel('order_action'),
      key: 'itemId',
      align: 'right',
      render: renderCodeButton,
    },
  ];

  // 绑定新手机号
  const renderUpdateContent = () => {
    if (updateDataList.length) {
      return (
        <>
          <div className={styles.messageText}>
            {renderIntlLabel('guest_label_modify_mobile_bind_soon')}
          </div>
          <Table
            rowKey="itemId"
            columns={updateColumns}
            dataSource={updateDataList}
            pagination={false}
            loading={loading}
          />
        </>
      );
    }
    return null;
  };

  const changeColumns: ColumnProps<TableRecord>[] = [
    { title: renderIntlLabel('order_roomorder_name'), dataIndex: 'name', width: 140 },
    {
      title: renderIntlLabel('channel_type'),
      dataIndex: 'contractType',
      render: (val) => ContractTypeMap[val],
      width: 140,
    },
    {
      title: renderIntlLabel('help_store_address_table_column_detail'),
      dataIndex: 'content',
      render: (val, record) =>
        record.contractType === ContractType.MOBILE ? formatAreaMobileStr(val) : val,
      width: 180,
    },
    {
      title: renderIntlLabel('order_roomorder_original_registered_user'),
      dataIndex: 'oriName',
      width: 140,
      render: (val, record) =>
        Array.isArray(record?.groupInfo) && record?.groupInfo?.length >= 2
          ? renderIntlLabel('guest_title_verify_mobile_ori_binding_counts', {
              count: record?.groupInfo?.length,
            })
          : val,
    },
    {
      title: renderIntlLabel('order_roomorder_mobile_verification_code'),
      key: 'smsCode',
      width: 140,
      render: renderCodeInput,
    },
    {
      title: renderIntlLabel('order_action'),
      key: 'itemId',
      align: 'right',
      render: renderCodeButton,
    },
  ];

  // 换绑
  const renderChangeContent = () => {
    if (changeDataList.length) {
      return (
        <>
          <div className={styles.messageText}>
            {renderIntlLabel('guest_label_input_code_to_exchange_bind_existing_mobile')}
          </div>
          <Table
            rowKey="itemId"
            columns={changeColumns}
            dataSource={changeDataList}
            pagination={false}
            loading={loading}
          />
        </>
      );
    }
    return null;
  };

  return (
    <Modal
      title={renderIntlLabel('guest_title_verify_mobile_binding')}
      wrapClassName={styles.phoneCheckWrapper}
      width={900}
      maskClosable={false}
      open={visible}
      confirmLoading={loading}
      onCancel={callBindCancel}
      footer={
        <div>
          <Button onClick={callBindCancel}>{renderIntlLabel('order_action_cancel')}</Button>
          <ThrottleButton type="primary" loading={loading} onClick={handleBatchSendClick}>
            {renderIntlLabel('guest_action_send_all_verfication_code')}
          </ThrottleButton>
          <ThrottleButton type="primary" loading={loading} onClick={handleConfirmClick}>
            {renderIntlLabel('guest_action_confirm_binding')}
          </ThrottleButton>
        </div>
      }
    >
      <div className={styles.tableWrapper}>{renderUpdateContent()}</div>
      <div className={styles.tableWrapper}>{renderChangeContent()}</div>
    </Modal>
  );
};

export default Index;
